import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/Logo";
import { ArrowRight, ArrowLeft, Music } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Identità", "Settore", "Bio & Intervista"];

const Onboarding = () => {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [data, setData] = useState({
        headline: "",
        industry: "",
        location: "",
        bio: "",
        skills: "",
        spotify_episode_url: "",
        website: "",
        linkedin: "",
    });
    const [loading, setLoading] = useState(false);

    if (!user) return <Navigate to="/login" replace />;
    if (user.onboarded) return <Navigate to="/feed" replace />;

    const next = () => setStep(Math.min(step + 1, STEPS.length - 1));
    const prev = () => setStep(Math.max(step - 1, 0));

    const submit = async () => {
        // Spotify URL validation (only if provided)
        const spot = data.spotify_episode_url.trim();
        if (spot && !spot.startsWith("https://open.spotify.com/")) {
            toast.error("Il link Spotify deve iniziare con https://open.spotify.com/");
            return;
        }
        setLoading(true);
        // Sito e LinkedIn (facoltativi): salvati prima, così un link sbagliato si può correggere qui
        if (data.website.trim() || data.linkedin.trim()) {
            try {
                await api.patch("/profile", { website: data.website, linkedin: data.linkedin });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Controlla sito web e LinkedIn");
                setLoading(false);
                return;
            }
        }
        try {
            await api.post("/auth/onboarding", {
                headline: data.headline,
                industry: data.industry,
                location: data.location,
                bio: data.bio,
                skills: data.skills.split(",").map((s) => s.trim()).filter(Boolean),
            });
            // Save Spotify URL via profile patch (separate endpoint)
            if (spot) {
                try { await api.patch("/profile", { spotify_episode_url: spot }); } catch {}
            }
            await refresh();
            toast.success("Profilo creato. Benvenuto nel network.");
            nav("/feed");
        } catch (e) {
            toast.error("Errore durante il salvataggio");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-bg flex flex-col">
            <div className="max-w-2xl mx-auto px-4 py-10 w-full">
                <Logo />
                <div className="mt-10 bg-paper arch-card border border-border-line soft-shadow-lg p-8 md:p-10">
                    <div className="flex items-center gap-2 mb-6">
                        {STEPS.map((s, i) => (
                            <div key={s} className="flex-1">
                                <div className={`h-1.5 rounded-full ${i <= step ? "bg-bordeaux" : "bg-surface"}`} />
                                <div className={`text-xs mt-2 uppercase tracking-widest ${i <= step ? "text-ink" : "text-muted"}`}>{s}</div>
                            </div>
                        ))}
                    </div>
                    <h1 className="font-heading text-4xl mb-2">Costruiamo il tuo profilo</h1>
                    <p className="text-sm text-muted mb-8">Ciao {user.full_name}, dicci di te.</p>

                    {step === 0 && (
                        <div className="space-y-4 fade-up">
                            <div>
                                <label className="text-xs uppercase tracking-widest text-muted">Headline professionale</label>
                                <input data-testid="onb-headline" className="input-clean mt-1" placeholder="es. Architetto · Studio di interni a Milano" value={data.headline} onChange={(e) => setData({ ...data, headline: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-muted">Località</label>
                                <input data-testid="onb-location" className="input-clean mt-1" placeholder="es. Milano, Italia" value={data.location} onChange={(e) => setData({ ...data, location: e.target.value })} />
                            </div>
                        </div>
                    )}
                    {step === 1 && (
                        <div className="space-y-4 fade-up">
                            <div>
                                <label className="text-xs uppercase tracking-widest text-muted">Settore / Categoria</label>
                                <input data-testid="onb-industry" className="input-clean mt-1" placeholder="es. Architettura, Consulenza, Food, Tech…" value={data.industry} onChange={(e) => setData({ ...data, industry: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-muted">Competenze (separa con virgola)</label>
                                <input data-testid="onb-skills" className="input-clean mt-1" placeholder="es. Branding, Interior Design, Marketing" value={data.skills} onChange={(e) => setData({ ...data, skills: e.target.value })} />
                            </div>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="space-y-4 fade-up">
                            <div>
                                <label className="text-xs uppercase tracking-widest text-muted">Bio</label>
                                <textarea data-testid="onb-bio" className="input-clean mt-1 min-h-[140px]" placeholder="Raccontati in poche righe…" value={data.bio} onChange={(e) => setData({ ...data, bio: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-muted flex items-center gap-1">
                                    <Music size={12} /> Link alla tua intervista (Spotify) — facoltativo
                                </label>
                                <input
                                    data-testid="onb-spotify"
                                    type="url"
                                    className="input-clean mt-1"
                                    placeholder="https://open.spotify.com/episode/..."
                                    value={data.spotify_episode_url}
                                    onChange={(e) => setData({ ...data, spotify_episode_url: e.target.value })}
                                />
                                <div className="text-[11px] text-soft mt-1">Incolla l'URL di un episodio Spotify: comparirà come player sul tuo profilo pubblico.</div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-muted">Sito web — facoltativo</label>
                                    <input data-testid="onb-website" className="input-clean mt-1" placeholder="www.tuosito.it" value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-muted">Profilo LinkedIn — facoltativo</label>
                                    <input data-testid="onb-linkedin" className="input-clean mt-1" placeholder="linkedin.com/in/nome-cognome" value={data.linkedin} onChange={(e) => setData({ ...data, linkedin: e.target.value })} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between mt-8">
                        <button data-testid="onb-prev" onClick={prev} disabled={step === 0} className="btn-ghost text-sm flex items-center gap-1 disabled:opacity-30">
                            <ArrowLeft size={16} /> Indietro
                        </button>
                        {step < STEPS.length - 1 ? (
                            <button data-testid="onb-next" onClick={next} className="btn-primary text-sm">
                                Avanti <ArrowRight size={16} />
                            </button>
                        ) : (
                            <button data-testid="onb-submit" onClick={submit} disabled={loading} className="btn-primary text-sm">
                                {loading ? "Salvataggio…" : "Entra nel network"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Onboarding;
